import React, { useRef } from "react";
import {Badge, Button, Modal} from "react-bootstrap";
import {useDispatch, useSelector} from "react-redux";
import QR from "qrcode-base64";
import { MdPictureAsPdf } from "react-icons/md";
import {FaImage, FaLink} from "react-icons/fa";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import moment from "moment/moment";
import { dejavuSans } from "../../../../../fonts/DejaVuSans-normal";
import { dejavuSansBold } from "../../../../../fonts/DejaVuSans-Bold-bold";
import {FaShareFromSquare} from "react-icons/fa6";
import {IoDocumentText} from "react-icons/io5";
import axios from "axios";
import {openSaleLocal} from "../../../../../_helpers/tabOpeners";

const GroupQRModal = ({ show, setShow, groupData, message = true }) => {
    const dispatch = useDispatch();
    const company = useSelector((state) => state.COMPANY_DATA.company);
    const TABS_DATA = useSelector((state) => state.TABS_REDUCER);
    const qrImage = useRef(null);

    const generateQRCode = (str) => {
        return QR.drawImg(str, {
            typeNumber: 4,
            errorCorrectLevel: 'M',
            size: 300
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        return moment(dateString).format("DD/MM/YYYY HH:mm");
    };

    const saveAsJPG = () => {
        if (!qrImage.current || !groupData) return;

        const imgElement = qrImage.current;
        const canvas = document.createElement('canvas');
        canvas.width = 800;
        canvas.height = 1250;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const createdDateStr = formatDate(groupData.created_at);
        const expireDateStr = formatDate(groupData.expiresAt);
        const companyName = company.name || company.companyName || '-';
        const companyVat = company.vat || company.vatNumber || '-';
        const themeHex = '#76AEB7';
        const textDark = '#282828';

        ctx.strokeStyle = themeHex;
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.roundRect(30, 30, canvas.width - 60, canvas.height - 60, 20);
        ctx.stroke();
        ctx.fillStyle = themeHex;
        ctx.beginPath();
        ctx.roundRect(30, 30, canvas.width - 60, 80, [20, 20, 0, 0]);
        ctx.fill();
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 32px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText("Ομαδικό QR Code Δελτίων Αποστολής", canvas.width / 2, 85);
        ctx.font = '18px sans-serif';

        const labelX = 90;
        const valueX = 320;
        const maxTextWidth = canvas.width - valueX - 60;

        const getLines = (text, maxWidth) => {
            const words = String(text).split(' ');
            let lines = [];
            let currentLine = words[0] || '';

            for (let i = 1; i < words.length; i++) {
                const word = words[i];
                const width = ctx.measureText(currentLine + " " + word).width;
                if (width < maxWidth) {
                    currentLine += " " + word;
                } else {
                    lines.push(currentLine);
                    currentLine = word;
                }
            }
            if (currentLine) lines.push(currentLine);
            return lines;
        };

        const companyNameLines = getLines(companyName, maxTextWidth);
        const lineSpacing = 38;
        const totalLines = 3 + companyNameLines.length;
        const boxY = 150;
        const boxHeight = (totalLines * lineSpacing) + 20;

        ctx.fillStyle = '#f5fafc';
        ctx.strokeStyle = themeHex;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.roundRect(60, boxY, canvas.width - 120, boxHeight, 15);
        ctx.fill();
        ctx.stroke();
        ctx.textAlign = 'left';

        let currentY = boxY + 45;
        ctx.fillStyle = textDark;
        ctx.font = 'bold 18px sans-serif';
        ctx.fillText("Εταιρεία:", labelX, currentY);
        ctx.font = '18px sans-serif';
        companyNameLines.forEach((line) => {
            ctx.fillText(line, valueX, currentY);
            currentY += lineSpacing;
        });

        ctx.font = 'bold 18px sans-serif';
        ctx.fillText("Α.Φ.Μ.:", labelX, currentY);
        ctx.font = '18px sans-serif';
        ctx.fillText(companyVat, valueX, currentY);
        currentY += lineSpacing;

        ctx.font = 'bold 18px sans-serif';
        ctx.fillText("Ημ/νία Δημιουργίας:", labelX, currentY);
        ctx.font = '18px sans-serif';
        ctx.fillText(createdDateStr, valueX, currentY);
        currentY += lineSpacing;

        ctx.font = 'bold 18px sans-serif';
        ctx.fillText("Ημ/νία Λήξης:", labelX, currentY);
        ctx.font = '18px sans-serif';
        ctx.fillText(expireDateStr, valueX, currentY);

        const qrSize = canvas.width - 160;
        const qrX = (canvas.width - qrSize) / 2;
        const qrY = boxY + boxHeight + 50;

        ctx.strokeStyle = themeHex;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.roundRect(qrX - 20, qrY - 20, qrSize + 40, qrSize + 40, 15);
        ctx.stroke();
        ctx.drawImage(imgElement, qrX, qrY, qrSize, qrSize);

        const dataURL = canvas.toDataURL('image/jpeg', 1.0);
        const link = document.createElement('a');
        link.href = dataURL;
        link.download = "Group QR.jpg";
        link.click();
    };

    const saveAsPDF = () => {
        if (!qrImage.current || !groupData) return;

        try {
            const doc = new jsPDF('p', 'pt', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            doc.addFileToVFS("dejavusans.ttf", dejavuSans);
            doc.addFont("dejavusans.ttf", "DejaVu Sans Normal", "normal");
            doc.addFileToVFS("dejavusansBold.ttf", dejavuSansBold);
            doc.addFont("dejavusansBold.ttf", "DejaVu Sans Bold", "normal");

            const createdDateStr = formatDate(groupData.created_at);
            const expireDateStr = formatDate(groupData.expiresAt);
            const companyName = company.name || company.companyName || '-';
            const companyVat = company.vat || company.vatNumber || '-';
            const themeR = 118;
            const themeG = 174;
            const themeB = 183;

            doc.setDrawColor(themeR, themeG, themeB);
            doc.setLineWidth(2);
            doc.roundedRect(20, 20, pageWidth - 40, pageHeight - 40, 15, 15);
            doc.setFillColor(themeR, themeG, themeB);
            doc.roundedRect(20, 20, pageWidth - 40, 60, 15, 15, 'F');
            doc.rect(20, 50, pageWidth - 40, 30, 'F');
            doc.setFont("DejaVu Sans Bold");
            doc.setFontSize(22);
            doc.setTextColor(255, 255, 255);
            doc.text("Ομαδικό QR Code Δελτίων Αποστολής", pageWidth / 2, 60, { align: 'center' });

            const boxY = 100;
            const boxHeight = 130;
            doc.setFillColor(245, 250, 252);
            doc.setDrawColor(themeR, themeG, themeB);
            doc.setLineWidth(1);
            doc.roundedRect(40, boxY, pageWidth - 80, boxHeight, 10, 10, 'FD');
            doc.setFontSize(11);
            doc.setTextColor(40, 40, 40);

            const labelX = 60;
            const valueX = 240;
            const startTextY = boxY + 25;
            const lineSpacing = 28;

            doc.setFont("DejaVu Sans Bold");
            doc.text(`Εταιρεία:`, labelX, startTextY);
            doc.setFont("DejaVu Sans Normal");
            doc.text(`${companyName}`, valueX, startTextY);

            doc.setFont("DejaVu Sans Bold");
            doc.text(`Α.Φ.Μ.:`, labelX, startTextY + lineSpacing);
            doc.setFont("DejaVu Sans Normal");
            doc.text(`${companyVat}`, valueX, startTextY + lineSpacing);

            doc.setFont("DejaVu Sans Bold");
            doc.text(`Ημ/νία Δημιουργίας:`, labelX, startTextY + (lineSpacing * 2));
            doc.setFont("DejaVu Sans Normal");
            doc.text(`${createdDateStr}`, valueX, startTextY + (lineSpacing * 2));

            doc.setFont("DejaVu Sans Bold");
            doc.text(`Ημ/νία Λήξης:`, labelX, startTextY + (lineSpacing * 3));
            doc.setFont("DejaVu Sans Normal");
            doc.text(`${expireDateStr}`, valueX, startTextY + (lineSpacing * 3));

            const imgElement = qrImage.current;
            const canvas = document.createElement('canvas');
            canvas.width = imgElement.naturalWidth;
            canvas.height = imgElement.naturalHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(imgElement, 0, 0);

            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            const qrSize = pageWidth - 100;
            const qrX = (pageWidth - qrSize) / 2;
            const qrY = boxY + boxHeight + 40;

            doc.setDrawColor(themeR, themeG, themeB);
            doc.setLineWidth(1);
            doc.roundedRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20, 8, 8);
            doc.addImage(imgData, 'JPEG', qrX, qrY, qrSize, qrSize);

            doc.save("Group QR.pdf");
        } catch (error) {
            console.error("PDF Generation Error: ", error);
            toast.error("Παρουσιάστηκε σφάλμα κατά τη δημιουργία του PDF.");
        }
    };

    const handleOpen = (qrUrl) => {
        window.open(qrUrl, "_blank");
    }

    const handleCopy = (qrUrl) => {
        navigator.clipboard.writeText(qrUrl).then(() => {
            toast.info("Επιτυχής αντιγραφή συνδέσμου.", {autoClose: 1000});
        });
    }

    const handleOpenERP = (mark) => {
        axios.get(process.env.REACT_APP_API_URL2 + "/sale/get-sale-by-mark", {
            headers: { 'Content-Type': 'application/json' },
            params: {
                company: company.id,
                mark,
            }
        }).then((res) => {
            if (res.data.status === "200") {
                dispatch(openSaleLocal(res.data.data, TABS_DATA));
            } else {
                toast.error(res.data.message);
            }
        }).catch((err) => {
            console.log(err);
            toast.error("Σφάλμα κατά την αποστολή αιτήματος.");
        })
    }

    return (
        <Modal show={show} onHide={() => setShow(false)} backdrop={"static"} dialogClassName={"modal42PercentWidth"}>
            <Modal.Header closeButton>
                <Modal.Title>Group QR</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {groupData && (
                    <div style={{ textAlign: "center" }}>
                        {message && ("Το Group QR δημιουργήθηκε επιτυχώς!")}
                        <div style={{ maxWidth: "100%" }} className={"mt-2"}>
                            <img
                                ref={qrImage}
                                src={generateQRCode(groupData.groupQrUrl)}
                                alt="Group QR"
                                style={{
                                    border: "2px solid #76AEB7",
                                    borderRadius: "15px",
                                    padding: "10px",
                                    backgroundColor: "#ffffff"
                                }}
                            />
                        </div>
                        <div className="mt-2 w-100">
                            <div className="d-flex flex-wrap justify-content-center align-items-center" style={{ gap: "8px" }}>
                                <Button size="sm" onClick={saveAsPDF} className="text-nowrap d-flex align-items-center justify-content-center">
                                    Αποθήκευση ως PDF <MdPictureAsPdf className="ms-2"/>
                                </Button>
                                <Button size="sm" onClick={saveAsJPG} className="text-nowrap d-flex align-items-center justify-content-center">
                                    Αποθήκευση ως JPG <FaImage className="ms-2"/>
                                </Button>
                            </div>
                        </div>
                        {groupData?.invoicesTable?.length > 0 && (
                            <div className={"mt-2"}>
                                <table className={"simpleClassicTable"}>
                                    <colgroup>
                                        <col span={1} style={{width: "10%"}}></col>
                                        <col span={1} style={{width: "70%"}}></col>
                                        <col span={1} style={{width: "20%"}}></col>
                                    </colgroup>
                                    <thead>
                                    <tr>
                                        <th>Αρ.</th>
                                        <th>Παραστατικό</th>
                                        <th>Ενέργειες</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {groupData.invoicesTable.map((row, idx) => (
                                        <tr key={`gqrm-${idx}`}>
                                            <td>{idx + 1}</td>
                                            <td>{row.documentLabel}</td>
                                            <td>
                                                <Badge bg={"primary"} style={{cursor: "pointer", padding: "3px"}} onClick={() => handleOpen(row.qrUrl)} title={"Άνοιγμα Ιστοσελίδας"}>
                                                    <FaShareFromSquare size={16} />
                                                </Badge>
                                                <Badge bg={"primary"} className={"ml-2"} style={{cursor: "pointer", padding: "3px"}} onClick={() => handleCopy(row.qrUrl)} title={"Αντιγραφή συνδέσμου"}>
                                                    <FaLink size={16} />
                                                </Badge>
                                                {row.erpExists === true && (
                                                    <Badge bg={"primary"} className={"ml-2"} style={{cursor: "pointer", padding: "3px"}} onClick={() => handleOpenERP(row.invoiceMark)} title={"Προβολή παραστατικού"}>
                                                        <IoDocumentText size={16} />
                                                    </Badge>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-primary" onClick={() => setShow(false)}>
                    Κλείσιμο
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default GroupQRModal;